import { computed, watch, type MaybeRefOrGetter, toValue } from 'vue';
import type { AlreadyPlayedContent } from '@/helpers/api/metrics';
import useAssetThumbnails from '@/composables/useAssetThumbnails';

/**
 * Wraps the global useAssetThumbnails singleton with the report-specific
 * watcher: whenever the byMedia list changes, resolve thumbnails for the
 * new names and tell the singleton which names this scope still cares about
 * so it can prune the rest.
 *
 * Returns the live thumbnail map plus the failed-cache reset, the only two
 * things ReportsView needs to forward.
 */
export function useReportThumbnails(
  byMediaSource: MaybeRefOrGetter<AlreadyPlayedContent[] | undefined>,
) {
  const { thumbMap, resolveThumbnails, retainThumbnails, clearFailedThumbnails } =
    useAssetThumbnails();

  const mediaThumbInputs = computed(() =>
    (toValue(byMediaSource) ?? []).map((item) => ({
      name: item.media_name,
      url: item.media_url,
    })),
  );

  // Use media_id as the signature, not media_name — names can collide for
  // duplicate-named assets and would falsely de-duplicate cache invalidation.
  const mediaSignature = computed(() =>
    (toValue(byMediaSource) ?? []).map((item) => item.media_id).join('|'),
  );

  watch(
    mediaSignature,
    async () => {
      const inputs = mediaThumbInputs.value;
      if (inputs.length) await resolveThumbnails(inputs);
      retainThumbnails(inputs.map((i) => i.name));
    },
    { immediate: true },
  );

  return { thumbMap, clearFailedThumbnails };
}
